// Shared helpers for the deterministic statement parsers. Parsers operate on
// "pages" — each page an array of { s, x, y } text items (Latin/numeric only,
// Arabic stripped) with PDF coordinates. The pdf.js extractor (browser) and the
// dev harness (Node) both produce this shape, so parsers stay pure + testable.

export const toNum = (s) => {
  if (s == null) return null;
  const neg = /CR\b/i.test(s) ? false : /-/.test(s); // "123.45CR" = credit (positive owed-reducer); leading "-" = negative
  const n = parseFloat(String(s).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? (neg ? -n : n) : null;
};

// "123.45CR" → credit. Returns { amount, credit } where amount is unsigned.
export const amountWithSign = (s) => {
  const credit = /CR\b/i.test(s) || /^\s*-/.test(s);
  const n = parseFloat(String(s).replace(/[^0-9.]/g, ''));
  return { amount: Number.isFinite(n) ? n : null, credit };
};

// DD-MM-YYYY or DD/MM/YYYY → YYYY-MM-DD
export const dmyToISO = (s) => {
  const m = String(s).match(/(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/);
  if (!m) return null;
  let [, d, mo, y] = m;
  if (y.length === 2) y = '20' + y;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

// Group a page's items into rows by y-band (PDF y grows upward → sort desc).
export const rows = (items, band = 3) => {
  const map = new Map();
  for (const it of items) {
    const key = Math.round(it.y / band) * band;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  return [...map.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([y, cells]) => ({ y, cells: cells.sort((a, b) => a.x - b.x) }));
};

// Value to the right of (and on the same y-band as) a label substring.
// Returns the rightmost token matching `want` (default: a number/date).
export const fieldRight = (items, label, { minX = 380, band = 5, want = /[0-9]/ } = {}) => {
  // Prefer the exact label cell; else the shortest item containing it (avoids
  // matching the label embedded in a longer sentence elsewhere on the page).
  const matches = items.filter((i) => i.s.includes(label));
  if (!matches.length) return null;
  const lbl = matches.find((i) => i.s.trim() === label)
    || [...matches].sort((a, b) => a.s.length - b.s.length)[0];
  const cands = items
    .filter((i) => Math.abs(i.y - lbl.y) <= band && i.x >= minX && want.test(i.s) && i !== lbl)
    .sort((a, b) => a.x - b.x);
  return cands.length ? cands[cands.length - 1].s : null;
};

// Value directly BELOW a label (column layout: label on top, value under it,
// aligned by x). Must be used per-page (y-bands collide across pages).
export const fieldBelow = (items, label, { xtol = 45, maxDy = 45, want = /[0-9]/ } = {}) => {
  const matches = items.filter((i) => i.s.includes(label));
  if (!matches.length) return null;
  const lbl = matches.find((i) => i.s.trim() === label)
    || [...matches].sort((a, b) => a.s.length - b.s.length)[0];
  const cands = items
    .filter((i) => i !== lbl && want.test(i.s) && i.y < lbl.y && (lbl.y - i.y) <= maxDy && Math.abs(i.x - lbl.x) <= xtol)
    .sort((a, b) => (lbl.y - a.y) - (lbl.y - b.y));
  return cands.length ? cands[0].s : null;
};

// The page (items array) that contains a given label, else null.
export const pageWith = (pages, label) =>
  pages.find((p) => p.some((i) => i.s.includes(label))) || null;

